import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { ref, onValue, update, runTransaction } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";

const allStatuses = ["Recevé", "En Préparation", "Sortir en Livraison", "Livré", "Terminé", "Annulé"];

// إعادة المخزون عند إلغاء الطلب
const restockProducts = async (distributorId, orderId) => {
  try {
    const productsRef = ref(db, `distributors/${distributorId}/orders/${orderId}/products`);
    return new Promise((resolve) => {
      onValue(productsRef, async (snap) => {
        const products = snap.val() || {};
        const promises = Object.keys(products).map(productKey => {
          const product = products[productKey];
          const productId = product.id || product.productId;
          const quantity = product.quantity || 0;
          if (!productId || quantity <= 0) return null;
          const stockRef = ref(db, `distributors/${distributorId}/products/${productId}/quantity`);
          return runTransaction(stockRef, (currentQty) => (currentQty || 0) + quantity);
        });
        await Promise.all(promises);
        resolve();
      }, { onlyOnce: true });
    });
  } catch (err) {
    console.error("خطأ في إعادة المخزون:", err);
    throw err;
  }
};

// دالة لتحديث totalDebt للعميل المضاف فقط (clientsAdd)
const updateClientAddTotalDebt = async (distributorId, clientId) => {
  try {
    const ordersRef = ref(db, `distributors/${distributorId}/orders`);
    const ordersSnap = await new Promise((resolve) => {
      onValue(ordersRef, resolve, { onlyOnce: true });
    });
    
    const orders = ordersSnap.val() || {};
    let totalDebt = 0;
    
    Object.keys(orders).forEach(orderId => {
      const order = orders[orderId];
      if (order.clientId === clientId && order.status !== "Annulé") {
        totalDebt += Number(order.resteAmount) || 0;
      }
    });
    
    // تحديث totalDebt في clientsAdd
    const clientRef = ref(db, `distributors/${distributorId}/clientsAdd/${clientId}`);
    await update(clientRef, { 
      totalDebt: Number(totalDebt)
    });
    
    console.log(`✅ TotalDebt updated for client ${clientId}: ${totalDebt}`);
    return totalDebt;
  } catch (err) {
    console.error("❌ Error updating client totalDebt:", err);
  }
};

export default function ClientOrdersPage({ uid }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef();
  const PAGE_SIZE = 10;

  const [allOrders, setAllOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vendeursMap, setVendeursMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [restFilter, setRestFilter] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);

  // نافذة الدفع
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderTotal, setOrderTotal] = useState(0);
  const [paidAmount, setPaidAmount] = useState("");

  const remainingAmountCalc = Math.max(orderTotal - (Number(paidAmount) || 0), 0);

  // جلب البائعين
  useEffect(() => {
    const vendeursRef = ref(db, `distributors/${uid}/vendeurs`);
    const unsub = onValue(vendeursRef, snap => {
      const val = snap.val() || {};
      const map = {};
      Object.keys(val).forEach(key => {
        map[key] = `${val[key].name} ${val[key].lastName || ""}`.trim();
      });
      setVendeursMap(map);
    });
    return () => unsub();
  }, [uid]);

  // تحميل جميع طلبات الزبون
  useEffect(() => {
    if (!uid) return;
    setLoading(true);

    const ordersRef = ref(db, `distributors/${uid}/orders`);
    const unsub = onValue(ordersRef, snap => {
      const val = snap.val() || {};
      let list = Object.keys(val)
        .map(key => ({ id: key, ...val[key], createdAt: Number(val[key].createdAt) || 0 }))
        .filter(o => o.clientId === clientId);

      list.sort((a, b) => b.createdAt - a.createdAt);
      setAllOrders(list);
      setLoading(false);
    });

    return () => unsub();
  }, [uid, clientId]);

  // فلترة الطلبات
  useEffect(() => {
    if (!allOrders) return;
    let filtered = [...allOrders];

    if (statusFilter !== "all") filtered = filtered.filter(o => o.status === statusFilter);
    if (restFilter) filtered = filtered.filter(o => (o.resteAmount || 0) > 0);
    if (startDate) filtered = filtered.filter(o => o.createdAt >= new Date(startDate).getTime());
    if (endDate) filtered = filtered.filter(o => o.createdAt <= new Date(endDate).getTime());

    setNewOrdersCount(filtered.length);
    setOrders(filtered.slice(0, PAGE_SIZE));
    setHasMore(filtered.length > PAGE_SIZE);
  }, [allOrders, statusFilter, startDate, endDate, restFilter]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || loading || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) loadMoreOrders();
    };
    const container = containerRef.current || window;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [orders, hasMore, loading]);

  const loadMoreOrders = () => {
    if (!allOrders || loading) return;
    const filtered = getFilteredOrders();
    const nextOrders = filtered.slice(orders.length, orders.length + PAGE_SIZE);
    setOrders(prev => [...prev, ...nextOrders]);
    setHasMore(orders.length + nextOrders.length < filtered.length);
  };

  const getFilteredOrders = () => {
    let filtered = [...allOrders];
    if (statusFilter !== "all") filtered = filtered.filter(o => o.status === statusFilter);
    if (restFilter) filtered = filtered.filter(o => (o.resteAmount || 0) > 0);
    if (startDate) filtered = filtered.filter(o => o.createdAt >= new Date(startDate).getTime());
    if (endDate) filtered = filtered.filter(o => o.createdAt <= new Date(endDate).getTime());
    return filtered;
  };

  // تغيير حالة الطلب
  const updateOrderStatus = async (orderId, newStatus) => {
    const order = allOrders.find(o => o.id === orderId);
    if (!order) return;

    try {
      if (newStatus === "Terminé") {
        setOrderTotal(Number(order.resteAmount || order.finalPrice || 0));
        setSelectedOrderId(orderId);
        setShowPaymentDialog(true);
        return;
      }

      if (newStatus === "Annulé") {
        if (window.confirm("هل تريد إلغاء الطلب؟ سيتم إعادة المخزون تلقائياً.")) {
          await restockProducts(uid, orderId);
        }
        
        await update(ref(db, `distributors/${uid}/orders/${orderId}`), { status: newStatus });
        
        // تحديث totalDebt للعميل المضاف فقط
        await updateClientAddTotalDebt(uid, order.clientId);
        
      } else {
        await update(ref(db, `distributors/${uid}/orders/${orderId}`), { status: newStatus });
      }
      
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) { 
      console.error(err); 
    }
  };

  // تأكيد الدفع
  const confirmPayment = async () => {
    const order = orders.find(o => o.id === selectedOrderId);
    if (!order) return;

    const newPaid = (Number(order.paidAmount) || 0) + (Number(paidAmount) || 0);
    const total = Number(order.finalPrice ?? order.totalPrice ?? 0);
    const newReste = Math.max(total - newPaid, 0);

    await update(ref(db, `distributors/${uid}/orders/${selectedOrderId}`), {
      paidAmount: Number(newPaid),
      resteAmount: Number(newReste),
      status: "Terminé"
    });

    // تحديث totalDebt للعميل المضاف فقط
    await updateClientAddTotalDebt(uid, order.clientId);

    setOrders(prev => prev.map(o => o.id === selectedOrderId ? { 
      ...o, 
      paidAmount: newPaid, 
      resteAmount: newReste, 
      status: "Terminé" 
    } : o));
    
    setShowPaymentDialog(false);
    setPaidAmount("");
  };

  const getClientName = o => o.nameClientAdd || o.tradeName || "-";
  const getClientPhone = o => o.telClientAdd || o.phone || "-";
  const getClientAddress = o => o.adresseClientAdd || o.adresse || "-";

  const styles = {
    table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,.05)", fontFamily: "'Inter', roboto" },
    th: { background: "#f3f4f6", padding: 16, textAlign: "left", fontWeight: 600, fontSize: 14, color: "#111827", textTransform: "uppercase" },
    td: { padding: 16, borderBottom: "1px solid #e5e7eb", fontSize: 14, color: "#374151", transition: "background 0.2s" },
    trHover: { cursor: "default", transition: "background 0.2s" },
    input: { padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, width: "100%" },
    detailsButton: { background: "#3b82f6", color: "white", border: "none", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }
  };

  return (
    <div style={{ padding: 20, height: "80vh", overflowY: "auto" }} ref={containerRef}>
      <h2>Mes Commandes du client</h2>

      {/* Filter Bar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.input}>
          <option value="all">Tous les statuts</option>
          {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.input} />
        <label><input type="checkbox" checked={restFilter} onChange={e => setRestFilter(e.target.checked)} /> Commandes avec reste</label>
      </div>

      {/* Orders Table */}
      {orders.length === 0 && !loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>Aucune commande trouvée</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>N° commande</th>
              <th style={styles.th}>Client</th>
              <th style={styles.th}>Téléphone</th>
              <th style={styles.th}>Adresse</th>
              <th style={styles.th}>Total</th>
              <th style={styles.th}>Payé</th>
              <th style={styles.th}>Reste</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} style={{ ...styles.trHover, background: o.status === "Annulé" ? "#fee" : "white", cursor: "pointer" }} onClick={() => navigate(`/order/${uid}/${o.id}`)}>
                <td style={styles.td}>{o.orderNumber}</td>
                <td style={styles.td}>{getClientName(o)}</td>
                <td style={styles.td}>{getClientPhone(o)}</td>
                <td style={styles.td}>{getClientAddress(o)}</td>
                <td style={styles.td}>{Number(o.finalPrice || 0)} DA</td>
                <td style={{ ...styles.td, color: "#16a34a", fontWeight: "bold" }}>{Number(o.paidAmount || 0)} DA</td>
                <td 
                  style={{ 
                    ...styles.td, 
                    color: Number(o.resteAmount) > 0 ? "red" : "#16a34a", 
                    fontWeight: "bold", 
                    cursor: Number(o.resteAmount) > 0 ? "pointer" : "default" 
                  }} 
                  onClick={e => { 
                    e.stopPropagation(); 
                    if (Number(o.resteAmount) > 0) { 
                      setSelectedOrderId(o.id); 
                      setOrderTotal(Number(o.resteAmount)); 
                      setPaidAmount(""); 
                      setShowPaymentDialog(true); 
                    } 
                  }}
                >
                  {Number(o.resteAmount || 0)} DA
                </td>
                <td style={styles.td}>
                  {o.status === "Annulé" ? "Annulé" : (
                    <select 
                      value={o.status} 
                      onChange={e => updateOrderStatus(o.id, e.target.value)} 
                      style={styles.input} 
                      disabled={o.status === "Terminé"}
                    >
                      {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </td>
                <td style={styles.td}>{o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}</td>
                <td style={styles.td}>
                  <button 
                    onClick={e => { 
                      e.stopPropagation(); 
                      navigate(`/order/${uid}/${o.id}`); 
                    }} 
                    style={styles.detailsButton}
                  >
                    Détails
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Payment Dialog */}
      {showPaymentDialog && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"white", padding:30, borderRadius:12, width:350 }}>
            <h3>Paiement</h3>
            <div style={{marginBottom:10}}><b>Montant total:</b> {orderTotal} DA</div>
            <div style={{marginBottom:10}}>
              <label>Montant payé</label>
              <input 
                type="number" 
                value={paidAmount} 
                onChange={e => setPaidAmount(e.target.value)} 
                style={styles.input} 
              />
            </div>
            <div style={{marginBottom:20}}><b>Solde restant:</b> {remainingAmountCalc} DA</div>
            <div style={{display:"flex", gap:10}}>
              <button 
                onClick={confirmPayment} 
                style={{ background:"#16a34a", color:"white", border:"none", padding:"10px 15px", borderRadius:6, cursor:"pointer" }}
              >
                Valider
              </button>
              <button 
                onClick={() => setShowPaymentDialog(false)} 
                style={{ background:"#ef4444", color:"white", border:"none", padding:"10px 15px", borderRadius:6, cursor:"pointer" }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}